import React, { useMemo, useState } from "react";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import BookingPaymentHistoryTable from "./Components/BookingPaymentHistoryTable";
import { useRoleVerify } from "Common/RoleVerify";



/* =========================================================
   DATE HELPERS
========================================================= */

const formatDate = (date) => {
  if (!date) return "";

  // Already YYYY-MM-DD
  if (
    typeof date === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(date)
  ) {
    return date;
  }

  // DD/MM/YYYY
  if (
    typeof date === "string" &&
    /^\d{2}\/\d{2}\/\d{4}$/.test(date)
  ) {
    const [day, month, year] = date.split("/");

    return `${year}-${month}-${day}`;
  }

  const d = new Date(date);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/* =========================================================
   RANGE PARSER

   Supports:
   1. "17/08/2026 to 20/08/2026"
   2. "17/08/2026"
   3. ["17/08/2026", "20/08/2026"]
   4. [Date, Date]
   5. Date
========================================================= */

const parseRangeValue = (value) => {
  if (!value) {
    return {
      start_date: "",
      end_date: "",
    };
  }

  /* -----------------------------------------
     ARRAY
  ----------------------------------------- */

  if (Array.isArray(value)) {
    const start = value[0] || "";
    const end = value[1] || value[0] || "";

    return {
      start_date: formatDate(start),
      end_date: formatDate(end),
    };
  }

  /* -----------------------------------------
     STRING
  ----------------------------------------- */

  if (typeof value === "string") {
    const cleanValue = value.trim();

    if (!cleanValue) {
      return {
        start_date: "",
        end_date: "",
      };
    }

    const parts = cleanValue
      .split(/\s+to\s+/i)
      .map((item) => item.trim())
      .filter(Boolean);

    const startDate = formatDate(parts[0]);

    const endDate = parts[1]
      ? formatDate(parts[1])
      : startDate;

    return {
      start_date: startDate,
      end_date: endDate,
    };
  }

  /* -----------------------------------------
     SINGLE DATE OBJECT
  ----------------------------------------- */

  if (value instanceof Date) {
    const date = formatDate(value);

    return {
      start_date: date,
      end_date: date,
    };
  }

  return {
    start_date: "",
    end_date: "",
  };
};

/* =========================================================
   PAYMENT METHODS
========================================================= */


/* =========================================================
   COMPONENT
========================================================= */

const BookingPaymentHistoryList = () => {
  const today = new Date();

  const currentYear = today.getFullYear();

  const canShowAction = useRoleVerify("payment-history-show", ["can_access"]);


  const BOOKING_STATUS_OPTIONS = [
    { value: 0, label: "Pending", color: "#997404" },
    { value: 1, label: "Available", color: "#198754" },
    { value: 2, label: "Booked", color: "#0d6efd" },
    { value: 3, label: "Cleaning", color: "#20c997" },
    { value: 4, label: "Check In", color: "#667eea" },
    { value: 5, label: "Check Out", color: "#6f42c1" },
    { value: 6, label: "Dirty", color: "#a16207" },
    { value: 7, label: "Blocked", color: "#dc3545" },
    { value: 8, label: "Do Not Reserve", color: "#b91c1c" },
    { value: 9, label: "Occupied", color: "#495057" },
  ];

  const [bookingStatus, setBookingStatus] = useState([]);

  const handleBookingStatusToggle = (value) => {
    setBookingStatus((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)   // deselect
        : [...prev, value]                  // select
    );
  };


  const [paymentMethodOptions, setPaymentMethodOptions] = useState([
    {
      value: "",
      label: "All Payment Methods",
    },
  ]);

  const [taxSummary, setTaxSummary] =
    useState([]);

  const currentMonth = String(
    today.getMonth() + 1
  ).padStart(2, "0");

  const currentMonthValue =
    `${currentYear}-${currentMonth}`;

  /* =======================================================
     FILTER TYPE
  ======================================================= */

  const [filterType, setFilterType] =
    useState("month");

  /* =======================================================
     FILTER VALUES
  ======================================================= */

  const [yearValue, setYearValue] = useState(
    String(currentYear)
  );

  const [monthValue, setMonthValue] = useState(
    currentMonthValue
  );

  const [rangeValue, setRangeValue] = useState("");

  /* =======================================================
     PAYMENT METHOD
  ======================================================= */

  const [paymentMethod, setPaymentMethod] =
    useState("");

  /* =======================================================
     PAYMENT SUMMARY
  ======================================================= */

  const [paymentSummary, setPaymentSummary] =
    useState({
      totalAdvance: 0,
      totalRefund: 0,
    });

  /* =======================================================
     YEAR OPTIONS
  ======================================================= */

  const yearOptions = useMemo(() => {
    const years = [];

    for (
      let year = currentYear - 10;
      year <= currentYear + 10;
      year++
    ) {
      years.push(year);
    }

    return years;
  }, [currentYear]);

  /* =======================================================
     BUILD FILTERS

     IMPORTANT:
     Only ONE date filter is sent.
  ======================================================= */

  const filters = useMemo(() => {
    const baseFilters = {};

    if (paymentMethod) {
      baseFilters.payment_method = paymentMethod;
    }

    if (bookingStatus.length > 0) {
      baseFilters.booking_status = bookingStatus.join(",");
    }

    if (filterType === "year") {
      return { ...baseFilters, year: String(yearValue || ""), month: "", start_date: "", end_date: "" };
    }

    if (filterType === "month") {
      if (!monthValue) {
        return { ...baseFilters, year: "", month: "", start_date: "", end_date: "" };
      }
      const [year, month] = String(monthValue).split("-");
      return { ...baseFilters, year: year || "", month: month || "", start_date: "", end_date: "" };
    }

    if (filterType === "range") {
      const { start_date, end_date } = parseRangeValue(rangeValue);
      return { ...baseFilters, year: "", month: "", start_date: start_date || "", end_date: end_date || "" };
    }

    return { ...baseFilters, year: "", month: "", start_date: "", end_date: "" };
  }, [
    filterType,
    yearValue,
    monthValue,
    rangeValue,
    paymentMethod,
    bookingStatus, // was missing
  ]);

  /* =======================================================
     HANDLERS
  ======================================================= */

  const handleYearChange = (e) => {
    const value =
      e?.target?.value ??
      e?.value ??
      e ??
      "";

    setYearValue(String(value));
  };

  const handleMonthChange = (e) => {
    const value =
      e?.target?.value ??
      e?.value ??
      e ??
      "";

    setMonthValue(String(value));
  };

  /* =======================================================
     RANGE CHANGE

     This is the important fix.

     Supports:
     event
     string
     array
     Date
  ======================================================= */

  const handleRangeChange = (e) => {
    let value = "";

    /* Event object */

    if (
      e &&
      typeof e === "object" &&
      !Array.isArray(e) &&
      e.target
    ) {
      value =
        e.target.value ??
        "";
    }

    /* Object with value */

    else if (
      e &&
      typeof e === "object" &&
      !Array.isArray(e) &&
      "value" in e
    ) {
      value = e.value ?? "";
    }

    /* Array */

    else if (Array.isArray(e)) {
      value = e;
    }

    /* String / Date */

    else {
      value = e ?? "";
    }

    setRangeValue(value);
  };

  const handlePaymentMethodChange = (e) => {
    const value =
      e?.target?.value ??
      e?.value ??
      e ??
      "";

    setPaymentMethod(String(value));
  };

  /* =======================================================
     FILTER TYPE
  ======================================================= */

  const handleFilterTypeChange = (type) => {
    setFilterType(type);

    /*
      Clear previous date filters immediately.
      This guarantees only the selected filter
      is used.
    */

    if (type === "year") {
      setRangeValue("");
    }

    if (type === "month") {
      setRangeValue("");
    }

    if (type === "range") {
      /*
        Keep existing range if user already selected one.
      */
    }
  };

  /* =======================================================
     SUMMARY
  ======================================================= */

  const totalAdvance = Number(
    paymentSummary?.totalAdvance || 0
  );

  const totalRefund = Number(
    paymentSummary?.totalRefund || 0
  );

  const revenue =
    totalAdvance - totalRefund;

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <PageBody
      card={true}
      headerchildren={
        <>
          <HeadingOne>
            Booking Payment History
          </HeadingOne>

          <span
            className="badge fw-semibold px-3 py-2 d-flex align-items-center gap-2"
            style={{
              background: "#f8f9ff",
              color: "#667eea",
              border: "1px solid #c5cdf7",
              borderRadius: 20,
              fontSize: 13,
            }}
          >
            <i className="ti ti-history" />
            All payment records
          </span>
        </>
      }
    >
      <div className="col-12">

        {/* =================================================
            SUMMARY
        ================================================= */}


        {canShowAction && (
          <div className="row g-3 mb-3">

            {/* TOTAL ADVANCE */}
            <div className="col-md-4">
              <div
                className="card border-0 shadow-sm h-100"
                style={{
                  borderRadius: "10px",
                  background: "#f0fdf4",
                }}
              >
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div>
                    <div
                      className="text-muted fw-medium mb-1"
                      style={{ fontSize: 14 }}
                    >
                      Total Advance
                    </div>

                    <h4
                      className="mb-0 fw-bold"
                      style={{ color: "#16a34a" }}
                    >
                      ₹{" "}
                      {Number(totalAdvance || 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </h4>
                  </div>

                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: 45,
                      height: 45,
                      borderRadius: "50%",
                      background: "#dcfce7",
                      color: "#16a34a",
                      fontSize: 22,
                    }}
                  >
                    <i className="ti ti-arrow-down-left" />
                  </div>
                </div>
              </div>
            </div>

            {/* TOTAL REFUND */}
            <div className="col-md-4">
              <div
                className="card border-0 shadow-sm h-100"
                style={{
                  borderRadius: "10px",
                  background: "#fff1f2",
                }}
              >
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div>
                    <div
                      className="text-muted fw-medium mb-1"
                      style={{ fontSize: 14 }}
                    >
                      Total Refund
                    </div>

                    <h4
                      className="mb-0 fw-bold"
                      style={{ color: "#ef4444" }}
                    >
                      ₹{" "}
                      {Number(totalRefund || 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </h4>
                  </div>

                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: 45,
                      height: 45,
                      borderRadius: "50%",
                      background: "#ffe4e6",
                      color: "#ef4444",
                      fontSize: 22,
                    }}
                  >
                    <i className="ti ti-arrow-up-right" />
                  </div>
                </div>
              </div>
            </div>

            {/* REVENUE */}
            <div className="col-md-4">
              <div
                className="card border-0 shadow-sm h-100"
                style={{
                  borderRadius: "10px",
                  background: "#f0f7ff",
                }}
              >
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div>
                    <div
                      className="text-muted fw-medium mb-1"
                      style={{ fontSize: 14 }}
                    >
                      Revenue
                    </div>

                    <h4
                      className="mb-0 fw-bold"
                      style={{
                        color: revenue >= 0 ? "#0d6efd" : "#dc3545",
                      }}
                    >
                      ₹{" "}
                      {Number(revenue || 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </h4>
                  </div>

                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: 45,
                      height: 45,
                      borderRadius: "50%",
                      background: "#dbeafe",
                      color: "#0d6efd",
                      fontSize: 22,
                    }}
                  >
                    <i className="ti ti-chart-line" />
                  </div>
                </div>
              </div>
            </div>

            {/* TAX SUMMARY */}
            {taxSummary.map((tax) => (
              <div
                className="col-md-4"
                key={tax.tax_name}
              >
                <div
                  className="card border-0 shadow-sm h-100"
                  style={{
                    borderRadius: "10px",
                    background: "#f8f9ff",
                  }}
                >
                  <div className="card-body d-flex align-items-center justify-content-between">
                    <div>
                      <div
                        className="text-muted fw-medium mb-1"
                        style={{ fontSize: 14 }}
                      >
                        Total {tax.tax_name}
                      </div>

                      <h4
                        className="mb-0 fw-bold"
                        style={{ color: "#667eea" }}
                      >
                        ₹{" "}
                        {Number(tax.totalAmount || 0).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </h4>

                      <small className="text-muted">
                        Rate: {Number(tax.rate || 0)}%
                      </small>
                    </div>

                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width: 45,
                        height: 45,
                        borderRadius: "50%",
                        background: "#e8ecff",
                        color: "#667eea",
                        fontSize: 20,
                      }}
                    >
                      <i className="ti ti-receipt-tax" />
                    </div>
                  </div>
                </div>
              </div>
            ))}

          </div>
        )}


        {/* =================================================
            FILTER BAR
        ================================================= */}

        <div
          className="card border-0 shadow-sm mb-3"
          style={{
            borderRadius: 10,
            background: "#fff",
          }}
        >
          <div className="card-body p-3">

            {/* FILTER CONTROLS */}
            <div className="row g-3 align-items-end">

              {/* FILTER TYPE */}
              <div className="col-xl-4 col-lg-5 col-md-6">
                <label
                  className="form-label"
                  style={{
                    color: "#212529",
                    fontSize: "13px",
                    fontWeight: 600,
                    lineHeight: "18px",
                    marginBottom: "6px",
                  }}
                >
                  Filter Type
                </label>

                <div
                  className="d-flex"
                  style={{
                    height: 38,
                    background: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: 6,
                    padding: 3,
                  }}
                >
                  {[
                    ["year", "Year"],
                    ["month", "Month"],
                    ["range", "Date Range"],
                  ].map(([value, label]) => {
                    const active = filterType === value;

                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          handleFilterTypeChange(value)
                        }
                        style={{
                          flex: 1,
                          height: 30,
                          border: 0,
                          borderRadius: 4,
                          background: active
                            ? "#ffffff"
                            : "transparent",
                          color: active
                            ? "#0d6efd"
                            : "#6c757d",
                          fontSize: "13px",
                          fontWeight: active ? 600 : 500,
                          lineHeight: "18px",
                          boxShadow: active
                            ? "0 1px 3px rgba(0,0,0,.08)"
                            : "none",
                          cursor: "pointer",
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* PERIOD */}
              <div
                className={
                  filterType === "range"
                    ? "col-xl-3 col-lg-4 col-md-6"
                    : "col-xl-2 col-lg-3 col-md-6"
                }
              >
                <label
                  className="form-label"
                  style={{
                    color: "#212529",
                    fontSize: "13px",
                    fontWeight: 600,
                    lineHeight: "18px",
                    marginBottom: "6px",
                  }}
                >
                  {filterType === "year"
                    ? "Year"
                    : filterType === "month"
                      ? "Month"
                      : "Date Range"}
                </label>

                {filterType === "year" && (
                  <select
                    className="form-select form-select-sm"
                    value={yearValue}
                    onChange={handleYearChange}
                    style={{
                      height: 38,
                      color: "#212529",
                      fontSize: "13px",
                      fontWeight: 500,
                      borderColor: "#dee2e6",
                      borderRadius: 6,
                      backgroundColor: "#fff",
                    }}
                  >
                    {yearOptions.map((year) => (
                      <option
                        key={year}
                        value={String(year)}
                      >
                        {year}
                      </option>
                    ))}
                  </select>
                )}

                {filterType === "month" && (
                  <FormDatePicker
                    field={{
                      name: "payment_month",
                      fieldSize: "p-0",
                      placeholder: "Select Month",
                      lableHide: true,
                    }}
                    value={monthValue}
                    handleChange={handleMonthChange}
                    mode="monthYear"
                  />
                )}

                {filterType === "range" && (
                  <FormDatePicker
                    field={{
                      name: "payment_date",
                      fieldSize: "p-0",
                      placeholder: "Select Date Range",
                      lableHide: true,
                    }}
                    value={rangeValue}
                    handleChange={handleRangeChange}
                    selectMode="range"
                    dateFormat="d/m/Y"
                  />
                )}
              </div>

              {/* PAYMENT METHOD */}
              <div className="col-xl-3 col-lg-4 col-md-6">
                <label
                  className="form-label"
                  style={{
                    color: "#212529",
                    fontSize: "13px",
                    fontWeight: 600,
                    lineHeight: "18px",
                    marginBottom: "6px",
                  }}
                >
                  Payment Method
                </label>

                <select
                  className="form-select form-select-sm"
                  value={paymentMethod}
                  onChange={handlePaymentMethodChange}
                  style={{
                    height: 38,
                    color: "#212529",
                    fontSize: "13px",
                    fontWeight: 500,
                    borderColor: "#dee2e6",
                    borderRadius: 6,
                    backgroundColor: "#fff",
                  }}
                >
                  {paymentMethodOptions.map((option) => (
                    <option
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

            </div>

            {/* BOOKING STATUS */}
            <div
              className="mt-3 pt-3"
              style={{
                borderTop: "1px solid #f0f1f2",
              }}
            >
              <div className="d-flex align-items-center flex-wrap gap-2">

                <span
                  style={{
                    color: "#212529",
                    fontSize: "13px",
                    fontWeight: 600,
                    lineHeight: "18px",
                    marginRight: 6,
                  }}
                >
                  Booking Status
                </span>

                {BOOKING_STATUS_OPTIONS.map((status) => {
                  const active = bookingStatus.includes(
                    status.value
                  );

                  return (
                    <button
                      key={status.value}
                      type="button"
                      onClick={() =>
                        handleBookingStatusToggle(
                          status.value
                        )
                      }
                      style={{
                        height: 30,
                        padding: "0 12px",
                        borderRadius: 5,
                        border: active
                          ? `1px solid ${status.color}`
                          : "1px solid #dee2e6",
                        background: active
                          ? status.color
                          : "#ffffff",
                        color: active
                          ? "#ffffff"
                          : "#495057",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: "16px",
                        cursor: "pointer",
                      }}
                    >
                      {status.label}
                    </button>
                  );
                })}

                {bookingStatus.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setBookingStatus([])}
                    style={{
                      height: 30,
                      padding: "0 11px",
                      border: "1px solid #dee2e6",
                      borderRadius: 5,
                      background: "#f8f9fa",
                      color: "#6c757d",
                      fontSize: "12px",
                      fontWeight: 500,
                      lineHeight: "16px",
                      cursor: "pointer",
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* =================================================
            TABLE
        ================================================= */}

        <BookingPaymentHistoryTable
          filters={filters}
          canShowAction={canShowAction}
          onSummaryChange={setPaymentSummary}
          onTaxSummaryChange={setTaxSummary}
          onPaymentMethodsChange={setPaymentMethodOptions}
        />

      </div>
    </PageBody >
  );
};

export default BookingPaymentHistoryList;